import { type JSX, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Divider, Stack, TextField, Typography } from '@mui/material';

import { passwordSignIn, requestPasswordEmail } from '../api.js';
import { GoogleSignInButton } from './GoogleSignInButton.js';

/**
 * Email + password sign-in — the primary way a returning customer gets
 * back into the Portal. The "email me a link" button below doubles as the
 * first-time / forgot-password recovery path (sends a set-password link).
 *
 * "Set or reset your password" reuses the same email field and asks the
 * server to email a one-time link (non-enumerating — same "check your
 * inbox" message whether or not the account exists). The link lands on
 * `?setpw=<token>`, which the App renders as the SetPassword screen.
 */
type View =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'sent' }
  | { kind: 'error'; message: string };

export function PasswordSignIn(props: {
  /** Called after the server accepts the password and sets the session
   *  cookie. `mfaRequired` is true when the account has 2FA enabled and the
   *  session is mfa_pending — the App shows the TOTP challenge instead of
   *  going straight to fleets. */
  onSignedIn: (mfaRequired: boolean) => void | Promise<void>;
  /** Email-first entry: prefill the email captured by the gate. */
  initialEmail?: string;
  /** Hide the email field (the gate already shows it). */
  hideEmail?: boolean;
}): JSX.Element {
  const [email, setEmail] = useState(props.initialEmail ?? '');
  const [password, setPassword] = useState('');
  const [view, setView] = useState<View>({ kind: 'idle' });

  async function handleSignIn(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const trimmed = email.trim();
    if (trimmed.length === 0 || password.length === 0) return;
    setView({ kind: 'busy' });
    const res = await passwordSignIn({ email: trimmed, password });
    if (res.kind === 'ok') {
      await props.onSignedIn(res.mfaRequired);
      return;
    }
    if (res.kind === 'invalid') {
      setView({ kind: 'error', message: 'Invalid email or password.' });
      return;
    }
    if (res.kind === 'locked') {
      setView({ kind: 'error', message: res.message });
      return;
    }
    if (res.kind === 'network') {
      setView({ kind: 'error', message: `Network: ${res.message}` });
      return;
    }
    setView({ kind: 'error', message: `Server returned ${String(res.status)}.` });
  }

  async function handleEmailLink(): Promise<void> {
    const trimmed = email.trim();
    if (trimmed.length === 0) {
      setView({ kind: 'error', message: 'Enter your email first, then request a link.' });
      return;
    }
    setView({ kind: 'busy' });
    const res = await requestPasswordEmail(trimmed, 'verify');
    if (res.kind === 'ok') {
      setView({ kind: 'sent' });
      return;
    }
    if (res.kind === 'invalid') {
      setView({ kind: 'error', message: res.message });
      return;
    }
    if (res.kind === 'network') {
      setView({ kind: 'error', message: `Network: ${res.message}` });
      return;
    }
    setView({ kind: 'error', message: `Server returned ${String(res.status)}.` });
  }

  if (view.kind === 'sent') {
    return (
      <Card variant="outlined" data-testid="password-signin">
        <CardContent>
          <Alert severity="success" role="status" sx={{ mb: 2 }}>
            <strong>Check your inbox.</strong> If that email is tied to a MobiLauncher account, a
            link to set your password is on its way. It stays valid for 30 minutes and only works
            once.
          </Alert>
          <Button onClick={() => setView({ kind: 'idle' })}>Back to sign in</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" data-testid="password-signin">
      <CardContent>
        <Typography variant="h6" fontWeight={700}>Sign in</Typography>
        <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
          Use the email you signed up with — by password or Google.
        </Typography>

        <Box component="form" onSubmit={(e) => void handleSignIn(e)}>
          <Stack spacing={2}>
            {props.hideEmail !== true && (
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourcompany.com"
                fullWidth
                required
                inputProps={{ maxLength: 200, autoComplete: 'email', 'data-testid': 'password-signin-email' }}
              />
            )}
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
              autoFocus={props.hideEmail === true}
              inputProps={{ maxLength: 200, autoComplete: 'current-password', 'data-testid': 'password-signin-password' }}
            />
            {view.kind === 'error' && <Alert severity="error" role="alert">{view.message}</Alert>}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={view.kind === 'busy' || email.trim().length === 0 || password.length === 0}
              data-testid="password-signin-submit"
              sx={{ py: 1 }}
            >
              {view.kind === 'busy' ? 'Signing in…' : 'Sign in'}
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => void handleEmailLink()}
              disabled={view.kind === 'busy'}
              data-testid="password-signin-email-link"
              sx={{ alignSelf: 'flex-start' }}
            >
              First time, or forgot your password? Email me a link.
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }}>or</Divider>
        <GoogleSignInButton disabled={view.kind === 'busy'} />
      </CardContent>
    </Card>
  );
}
